/***********************************************************************/
/*  predictive-search.js – versión corregida – 04-ago-2025             */
/*  Corrige: al escribir "la" (u otras stopwords) el input se borraba  */
/*  porque filteredSearchTerm quedaba vacío y close(true) limpiaba el  */
/*  campo. Ahora no se filtran stopwords en el predictivo y nunca se   */
/*  borra el texto del usuario al cerrar.                               */
/***********************************************************************/
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.statusElement = this.querySelector('.predictive-search-status');
    this.isOpen = false;
    this.abortController = null; // Para manejar solicitudes abortadas
    this.iconTimeout = null; // Para la animación del icono de búsqueda
    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    } else {
      console.warn('No se encontró el formulario con la clase "search" dentro de <predictive-search>.');
    }

    if (this.input) {
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
          this.animateSearchIcon();
        }, 300)
      );

      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('blur', this.onFocusOut.bind(this));
      this.input.addEventListener('keyup', this.onKeyup.bind(this));
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
    } else {
      console.warn('No se encontró el input de búsqueda dentro de <predictive-search>.');
    }

    // Delegar el manejo de clics en los resultados de búsqueda
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.addEventListener('click', this.onResultClick.bind(this));
    } else {
      console.warn('No se encontró el contenedor de resultados de búsqueda predictiva.');
    }
  }

  // Función para animar el icono de búsqueda
  animateSearchIcon() {
    const searchIcon = this.querySelector('#searchButton');
    if (searchIcon) {
      searchIcon.classList.add('animate-icon');
      clearTimeout(this.iconTimeout);
      this.iconTimeout = setTimeout(() => {
        searchIcon.classList.remove('animate-icon');
      }, 1000);
    } else {
      // Es opcional; si no existe, no interrumpe el flujo.
      // console.warn('No se encontró el botón de búsqueda con el ID "searchButton".');
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  // Función para normalizar cadenas y eliminar acentos
  normalizeString(str) {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }

  // Función para excluir palabras vacías (ÚSALA SÓLO EN SUBMIT)
  excludeStopWords(searchTerm) {
    const stopWords = ['de', 'la', 'el', 'y', 'en', 'para', 'con', 'a', 'por', 'un', 'una', 'del'];
    const words = this.normalizeString(searchTerm).split(/\s+/);
    const filteredWords = words.filter((word) => word.length > 0 && !stopWords.includes(word));
    return filteredWords.join(' ');
  }

  /*********************** ENTRADA DEL USUARIO (PREDICTIVO) ************************/
  onChange() {
    const rawSearchTerm = this.getQuery();
    const normalized = this.normalizeString(rawSearchTerm);

    // Si está vacío, cierra sin borrar el input
    if (!rawSearchTerm.length) {
      this.close(); // <- NO limpiar el campo
      return;
    }

    // *** IMPORTANTE ***
    // NO filtramos stopwords aquí. El predictivo debe funcionar con "la", "el", etc.
    // Así evitamos que "la" se convierta en vacío y borre el campo.
    this.getSearchResults(rawSearchTerm); // usar lo que el usuario escribe
  }

  onFormSubmit(event) {
    const rawQuery = this.getQuery();
    if (!rawQuery.length) {
      // No query, permitir envío (según comportamiento del tema)
      return;
    }

    // En submit, sí podemos intentar OR, pero sin romper consultas de 1 palabra.
    const normalized = this.normalizeString(rawQuery);
    const filtered = this.excludeStopWords(normalized);

    // Si al filtrar quedó vacío (ej: solo "la"), usa la consulta original normalizada
    const baseForOR = (filtered.length ? filtered : normalized);

    const searchQueryArray = baseForOR.split(/\s+/).filter(word => word.trim() !== '');
    const finalQuery = (searchQueryArray.length > 1)
      ? searchQueryArray.join(' OR ')
      : rawQuery; // con 1 palabra, mantenemos lo que el usuario escribió

    // Modificar el valor del input para incluir 'OR' sólo si aplica
    this.input.value = finalQuery;
    // Permitir que el formulario se envíe con la consulta modificada
  }

  onFocus() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.onChange();
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 100);
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(); // <- NO borrar el input
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (['ArrowUp', 'ArrowDown'].includes(event.code)) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.predictiveSearchResults.querySelector('[aria-selected="true"]');
    const allElements = this.predictiveSearchResults.querySelectorAll('li[role="option"]');
    let activeElement = this.predictiveSearchResults.querySelector('li[role="option"]');

    if (moveUp && !selectedElement) return;

    this.statusElement && (this.statusElement.textContent = '');

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedElement = this.predictiveSearchResults.querySelector('[aria-selected="true"]');

    if (selectedElement) {
      const link = selectedElement.querySelector('a');
      const button = selectedElement.querySelector('button.predictive-search__item--term');

      if (link) {
        // Si es un enlace de producto
        window.location.href = link.href;
      } else if (button) {
        // Si es el botón de búsqueda por palabras clave
        button.click();
      }
    }
  }

  onResultClick(event) {
    const button = event.target.closest('button.predictive-search__item--term');
    if (button) {
      const filteredQuery = button.getAttribute('data-filtered-query');
      if (filteredQuery) {
        // Redirigir a la página de resultados de búsqueda con la consulta filtrada que incluye "OR"
        window.location.href = `/search?q=${filteredQuery}`;
      }
    }
  }

  /*************************** FETCH DE RESULTADOS ***************************/
  getSearchResults(queryTerm) {
    const normalized = this.normalizeString(queryTerm);
    const queryKey = normalized.replace(/\s+/g, "-");
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      // Resultados desde caché
      this.renderSearchResults(this.cachedResults[queryKey], queryTerm);
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
      // console.log('Solicitud anterior abortada.');
    }

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    // Para predictivo, construir consulta:
    // - 1 palabra -> usar tal cual (no OR)
    // - 2+ palabras -> separar en OR (sin excluir stopwords)
    const parts = normalized.split(/\s+/).filter(Boolean);
    const searchQuery = (parts.length > 1) ? parts.join(' OR ') : queryTerm;

    // URL de búsqueda (Dawn/Shopify expone "routes.predictive_search_url")
    const baseURL = (window.routes && window.routes.predictive_search_url) ? window.routes.predictive_search_url : '/search/suggest';
    const searchURL = `${baseURL}?q=${encodeURIComponent(searchQuery)}&resources[type]=product&resources[limit]=4&section_id=predictive-search`;

    fetch(searchURL, { signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Error en la respuesta de la red: ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const resultsSection = doc.querySelector('#shopify-section-predictive-search');

        if (resultsSection) {
          const resultsMarkup = resultsSection.innerHTML;
          this.cachedResults[queryKey] = resultsMarkup;
          this.renderSearchResults(resultsMarkup, queryTerm);
        } else {
          // Si no hay sección de resultados, cerramos sin borrar input
          this.close();
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Error en getSearchResults:', error);
        }
        this.close();
      });
  }

  getIconArrowMarkup() {
    // SVG de ejemplo, no es obligatorio para el funcionamiento
    return `
      <svg aria-hidden="true" focusable="false" width="10" height="10" viewBox="0 0 10 10">
        <path d="M0 5h8M8 5l-3-3m3 3l-3 3" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/>
      </svg>
    `;
  }

  escapeHtml(text) {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
    return text.replace(/[&<>"']/g, function(m) { return map[m]; });
  }

  setLiveRegionLoadingState() {
    const loadingText = this.getAttribute('data-loading-text') || 'Cargando...';
    this.setLiveRegionText(loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (this.statusElement) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }
  }

  renderSearchResults(resultsMarkup, rawSearchTerm) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults(rawSearchTerm);
    this.open();
  }

  setLiveRegionResults(rawSearchTerm) {
    this.removeAttribute('loading');
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }

    // Anunciar la opción de "Buscar por [término]" si existe en el HTML existente
    const searchOption = this.querySelector('button.predictive-search__item--term');
    if (searchOption) {
      const displayedTerm = searchOption.textContent.match(/Buscar\s+[“"](.+)[”"]/i) || searchOption.textContent.match(/Buscar\s+"(.+)"/i);
      if (displayedTerm && displayedTerm[1]) {
        this.setLiveRegionText(`Opción adicional: Buscar por ${displayedTerm[1]}`);
      }
    }
  }

  getResultsMaxHeight() {
    const header = document.querySelector('.section-header');
    if (header) {
      this.resultsMaxHeight = window.innerHeight - header.getBoundingClientRect().bottom;
      return this.resultsMaxHeight;
    }
    return 300;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  // Por defecto NO limpiamos el texto del usuario
  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      // *** Evitar usar esta rama. La dejamos por compatibilidad, pero no se llama. ***
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.predictiveSearchResults.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    this.isOpen = false;
  }
}

// Función debounce
function debounce(fn, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

customElements.define('predictive-search', PredictiveSearch);
